import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

const SLIDE_DURATION_MS = 250;

interface WrongMovePopupProps {
  isOpen: boolean;
  variant?: 'wrongMove' | 'allLivesFinished';
}

const VARIANT_KEYS = {
  wrongMove: {
    title: 'game.wrongMovePopup.title',
    body: 'game.wrongMovePopup.body',
  },
  allLivesFinished: {
    title: 'game.allLivesFinished.title',
    body: 'game.allLivesFinished.body',
  },
} as const;

/**
 * Purely presentational — unlike the sibling implementations of this
 * component, it has no internal auto-dismiss timer. Its visibility is
 * derived entirely from the caller's existing phase state (ROUND_FAILURE),
 * which is already tied to the existing transitionDelay/levelDelay timeout
 * that decides when to advance or end the game. Adding a second, independent
 * timer here would just race the one that already exists. It only stays
 * mounted an extra SLIDE_DURATION_MS after isOpen flips false so the
 * slide-out transition (matching arrows' WrongMovePopup) has time to play.
 */
export function WrongMovePopup({
  isOpen,
  variant = 'wrongMove',
}: Readonly<WrongMovePopupProps>) {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
  const [slideIn, setSlideIn] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      const raf = requestAnimationFrame(() => { setSlideIn(true); });
      return () => { cancelAnimationFrame(raf); };
    }

    setSlideIn(false);
    const unmountTimer = setTimeout(() => { setMounted(false); }, SLIDE_DURATION_MS);
    return () => { clearTimeout(unmountTimer); };
  }, [isOpen]);

  if (!mounted) return null;

  const { title, body } = VARIANT_KEYS[variant];

  return createPortal(
    // Deliberately NOT pointer-events-none (unlike arrows): this overlay must
    // block taps on the board underneath for the full time it's visible,
    // same as any other modal in this app — it just has no buttons of its own.
    <div className="fixed inset-0 z-[9999] flex items-start justify-center px-5 pt-[40px]">
      <div
        className="relative max-w-[400px] w-full overflow-hidden rounded-[18px] border !border-white/15 shadow-[0_24px_60px_rgba(0,0,0,0.55)] transition-transform duration-[250ms] ease-out"
        style={{
          background: '#1c1b20',
          border: '1px solid #ff000070',
          boxShadow: '0px 0px 46px #ff000033',
          transform: slideIn ? 'translateY(0)' : 'translateY(-150%)',
        }}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/35 to-transparent" />

        <div className="flex flex-col p-5">
          <div className="!space-y-[6px]">
            <h3 className="cardTitle title-transparent">
              {t(title)}
            </h3>
            <p className="cardDescription">
              {t(body)}
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
